/**
 * Internationalization configuration and utilities
 */
import { createI18n, type I18n } from 'vue-i18n'
import axios from 'axios'

// Import locale messages
import en from '../i18n/locales/messages_en.json'
import fr from '../i18n/locales/messages_fr.json'
import ar from '../i18n/locales/messages_ar.json'

export const SUPPORTED_LOCALES = ['en', 'fr', 'ar'] as const
export type SupportedLocale = typeof SUPPORTED_LOCALES[number]

interface LocaleMessages {
  [key: string]: any
}

interface TranslationObject {
  [key: string]: any
}

/**
 * Get the appropriate locale based on saved preference, browser language, or default
 * @returns The locale to use
 */
export function getInitialLocale(): SupportedLocale {
  const savedLocale = localStorage.getItem('locale')
  const browserLocale = navigator.language.split('-')[0]
  const defaultLocale = savedLocale || browserLocale || 'en'
  
  return SUPPORTED_LOCALES.includes(defaultLocale as SupportedLocale) ? defaultLocale as SupportedLocale : 'en'
}

/**
 * Create and configure the i18n instance
 * @returns Configured i18n instance
 */
export function createI18nInstance(): I18n {
  const locale = getInitialLocale()

  if (typeof document !== 'undefined') {
    const isRtl = locale === 'ar'
    document.documentElement.setAttribute('lang', locale)
    document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr')
    document.body?.classList.toggle('rtl', isRtl)
  }
  
  return createI18n({
    legacy: false, // Use Composition API
    globalInjection: true, // Enable global injection (we can use $t in templates)
    locale,
    fallbackLocale: 'en',
    messages: {
      en,
      fr,
      ar
    },
    missing: (locale: string, key: string) => {
      // Silently handle missing translations to prevent console noise
      return key
    },
    numberFormats: {
      en: { currency: { style: 'currency', currency: 'USD' } },
      fr: { currency: { style: 'currency', currency: 'EUR' } },
      ar: { currency: { style: 'currency', currency: 'SAR' } }
    },
    datetimeFormats: {
      en: { short: { year: 'numeric', month: 'short', day: 'numeric' } },
      fr: { short: { year: 'numeric', month: 'short', day: 'numeric' } },
      ar: { short: { year: 'numeric', month: 'short', day: 'numeric' } }
    }
  })
}

/**
 * Deep merge translation objects
 * @param target - Target translation object
 * @param source - Source translation object
 * @returns Merged translation object
 */
function deepMerge(target: TranslationObject, source: TranslationObject): TranslationObject {
  if (!source || typeof source !== 'object') {
    return target
  }
  
  if (!target || typeof target !== 'object') {
    return Array.isArray(source) ? [...source] : { ...source }
  }
  
  if (Array.isArray(source)) {
    return [...source]
  }
  
  const result = { ...target }
  
  for (const [key, value] of Object.entries(source)) {
    if (value === undefined) continue
    
    if (value === null) {
      result[key] = null
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      result[key] = deepMerge(result[key], value)
    } else {
      result[key] = value
    }
  }
  
  return result
}

/**
 * Fetch and merge server-side translations
 * @param i18n - Vue i18n instance
 * @returns Success status
 */
export async function loadServerTranslations(i18n: I18n): Promise<boolean> {
  try {
    const response = await axios.get('/public/api/i18n/translations')
    
    if (!response.data) {
      return false
    }
    
    SUPPORTED_LOCALES.forEach(locale => {
      const serverTranslations = response.data[locale]
      if (!serverTranslations) return
      
      try {
        // Check if i18n and global are available
        if (!i18n || !i18n.global || typeof i18n.global.getLocaleMessage !== 'function') {
          console.warn(`i18n global instance not available for locale ${locale}`)
          return
        }
        
        const clientTranslations = i18n.global.getLocaleMessage(locale)
        let parsedServerTranslations = serverTranslations
        
        // Parse JSON string if necessary
        if (typeof serverTranslations === 'string') {
          const jsonStr = serverTranslations.replace(/^\uFEFF/, '') // Remove BOM
          parsedServerTranslations = JSON.parse(jsonStr)
        }
        
        const mergedTranslations = deepMerge(clientTranslations, parsedServerTranslations)
        console.log(`Merging translations for locale ${locale}:`, mergedTranslations)
        //i18n.global.setLocaleMessage(locale, mergedTranslations);
      } catch (error) {
        console.warn(`Failed to merge translations for locale ${locale}:`, error)
      }
    })
    
    return true
  } catch (error) {
    console.warn('Failed to load server translations:', error)
    return false
  }
}
